import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Shield, Award, Users, Target } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const values = [
  { icon: Shield, title: "Quality First", desc: "Every product undergoes rigorous quality testing before leaving our facility. We use only commercial-grade materials." },
  { icon: Award, title: "Australian Craftsmanship", desc: "Proudly manufactured in Western Australia, supporting local industry and delivering products built for Australian conditions." },
  { icon: Users, title: "Customer Partnership", desc: "We work closely with every client, from initial consultation through to installation and ongoing support." },
  { icon: Target, title: "Precision Engineering", desc: "Custom-built to exact specifications with advanced manufacturing processes ensuring perfect fit every time." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative py-32 sm:py-40 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
            className="max-w-3xl"
          >
            <motion.div variants={fadeUp} className="industrial-line mb-8" />
            <motion.h1
              variants={fadeUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6"
              style={{ fontFamily: "'Clash Display', sans-serif" }}
            >
              About <span className="gradient-text">NG Industries</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base sm:text-lg text-[#A3A3A3] max-w-xl leading-relaxed">
              Western Australia's leading manufacturer of industrial textile solutions, 
              delivering quality and performance since 1998.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
            >
              <motion.div variants={fadeUp} className="industrial-line mb-6" />
              <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                Our Story
              </motion.h2>
              <motion.p variants={fadeUp} className="text-[#A3A3A3] mb-6 leading-relaxed">
                Founded in 1998 in O'Connor, Western Australia, NG Industries began as a small workshop 
                specializing in PVC welding and industrial fabric repair. Over two decades later, we've 
                grown into one of Australia's most trusted industrial textile manufacturers.
              </motion.p>
              <motion.p variants={fadeUp} className="text-[#A3A3A3] mb-6 leading-relaxed">
                Our growth has been driven by a simple philosophy: build products that last. Every PVC wall, 
                industrial curtain, and protective cover that leaves our facility is engineered to withstand 
                the harshest conditions Australia's industries can throw at them.
              </motion.p>
              <motion.p variants={fadeUp} className="text-[#A3A3A3] mb-8 leading-relaxed">
                Today, we serve hundreds of businesses across warehousing, mining, manufacturing, logistics, 
                agriculture, and construction — providing custom-engineered solutions that improve safety, 
                efficiency, and workplace conditions.
              </motion.p>
              <motion.div variants={fadeUp}>
                <Link
                  to="/contact"
                  data-testid="about-contact-btn"
                  className="inline-flex items-center gap-2 bg-[#FF5722] text-white px-6 py-3 font-semibold text-sm hover:bg-[#E64A19] transition-colors group"
                >
                  Get in Touch <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </motion.div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxtYW51ZmFjdHVyaW5nJTIwZmFjdG9yeSUyMGZsb29yfGVufDB8fHx8MTc3OTY3NzQyNHww&ixlib=rb-4.1.0&q=85"
                alt="Manufacturing floor"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#FF5722] p-6 hidden lg:block">
                <p className="text-3xl font-bold text-white" style={{ fontFamily: "'Clash Display', sans-serif" }}>25+</p>
                <p className="text-sm text-white/80">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.div variants={fadeUp} className="text-center mb-16">
              <div className="industrial-line mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                Our Core Values
              </h2>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((v, i) => (
                <motion.div
                  key={v.title}
                  variants={fadeUp}
                  custom={i}
                  className="bg-[#0A0A0A] border border-[#2E2E2E] p-8 hover:border-[#FF5722] transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-[#FF5722]/10 flex items-center justify-center mb-6">
                    <v.icon size={24} className="text-[#FF5722]" />
                  </div>
                  <h3 className="text-lg font-bold mb-3" style={{ fontFamily: "'Clash Display', sans-serif" }}>{v.title}</h3>
                  <p className="text-sm text-[#A3A3A3] leading-relaxed">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://static.prod-images.emergentagent.com/jobs/f6a3f174-a193-4e97-9f7a-54fb8e64a616/images/c6219fe4f1590063ac540153a1f2177151af5acdf85baf614f322b307bf27f05.png"
                alt="Industrial PVC material"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
            >
              <motion.div variants={fadeUp} className="industrial-line mb-6" />
              <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                Safety & Compliance
              </motion.h2>
              <motion.p variants={fadeUp} className="text-[#A3A3A3] mb-6 leading-relaxed">
                All NG Industries products are manufactured in compliance with Australian Standards. 
                Our materials undergo rigorous testing for fire resistance, UV stability, and structural integrity.
              </motion.p>
              <motion.ul variants={fadeUp} className="space-y-4">
                {["Fire-resistant PVC materials (AS 1530)", "UV-stabilized for Australian conditions", "Workplace safety compliant (AS/NZS 4602)", "ISO 9001 quality management systems", "Environmental responsibility standards"].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-[#A3A3A3]">
                    <Shield size={16} className="text-[#FF5722] mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </motion.ul>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
